import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, X, ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';


const signInSchema = z.object({
  email: z.string().trim().email('Please enter a valid email address').max(255).toLowerCase(),
  password: z.string().min(1, 'Password is required').max(128),
});

const emailSchema = z.string().trim().email('Please enter a valid email address').max(255).toLowerCase();

type ModalView = 'sign-in' | 'forgot-password' | 'forgot-success' | 'reset-password' | 'mfa';

interface SignInModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRequestAccess: () => void;
}

const viewVariants = {
  initial: { opacity: 0, x: 20, filter: 'blur(3px)' },
  animate: { opacity: 1, x: 0, filter: 'blur(0px)', transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] as const } },
  exit: { opacity: 0, x: -20, filter: 'blur(3px)', transition: { duration: 0.18 } },
};

// Shared input className
const inputCls = "w-full rounded-md bg-background/50 border border-border/50 px-4 py-3 text-foreground outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/20 transition-all placeholder:text-muted-foreground/30 h-11 text-sm";
const inputFontStyle = { fontSize: '16px', caretColor: 'hsl(var(--gold))' } as const;

// Shared gold CTA
function GoldCTA({ children, disabled, type = 'submit', onClick, loading }: {
  children: React.ReactNode; disabled?: boolean; type?: 'submit' | 'button'; onClick?: () => void; loading?: boolean;
}) {
  return (
    <button
      type={type} disabled={disabled} onClick={onClick}
      className="relative w-full h-12 rounded-xl font-medium text-[13px] tracking-[0.1em] uppercase overflow-hidden transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed active:scale-[0.97] group"
      style={{
        background: 'linear-gradient(135deg, hsl(var(--gold) / 0.1), hsl(var(--gold) / 0.03))',
        border: '1px solid hsl(var(--gold) / 0.4)',
        color: 'hsl(var(--gold))',
        boxShadow: '0 0 24px -8px hsl(var(--gold) / 0.2), inset 0 1px 0 hsl(var(--gold) / 0.12)',
      }}
    >
      <span className="relative z-10 flex items-center justify-center gap-2">
        {loading && <span className="w-4 h-4 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />}
        {children}
      </span>
      <span className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-150" style={{ background: 'linear-gradient(135deg, hsl(var(--gold) / 0.15), transparent)' }} />
      <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.06] to-transparent -translate-x-full animate-[shimmerSweep_3s_ease-in-out_infinite]" />
    </button>
  );
}

export const SignInModal = ({ open, onOpenChange, onRequestAccess }: SignInModalProps) => {
  const [view, setView] = useState<ModalView>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Forgot password
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');

  // Reset password
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [touched, setTouched] = useState({ password: false, confirm: false });

  // MFA
  const [mfaCode, setMfaCode] = useState('');
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [pendingUser, setPendingUser] = useState<any>(null);

  // Use window.location instead of useSearchParams (modal is outside Router)
  const getSearchParam = (key: string) => new URLSearchParams(window.location.search).get(key);

  // Auto-open reset view if URL has token
  useEffect(() => {
    const token = getSearchParam('token');
    const type = getSearchParam('type');
    if (token && type === 'recovery' && open) {
      setView('reset-password');
    }
  }, [open]);

  const handleClose = () => {
    if (!loading) {
      onOpenChange(false);
      // Reset after animation
      setTimeout(() => {
        setView('sign-in');
        setEmail(''); setPassword(''); setError(null);
        setForgotEmail(''); setForgotError('');
        setNewPassword(''); setConfirmPassword('');
        setTouched({ password: false, confirm: false });
        setMfaCode(''); setMfaFactorId(null); setMfaError(null);
        setPendingUser(null);
      }, 300);
    }
  };

  const logLoginHistory = async (user: any) => {
    try {
      const userAgent = navigator.userAgent;
      const getBrowser = () => {
        if (userAgent.includes('Firefox')) return 'Firefox';
        if (userAgent.includes('Edg')) return 'Edge';
        if (userAgent.includes('Chrome')) return 'Chrome';
        if (userAgent.includes('Safari')) return 'Safari';
        return 'Unknown';
      };
      const getDeviceType = () => {
        if (/mobile/i.test(userAgent)) return 'Mobile';
        if (/tablet/i.test(userAgent)) return 'Tablet';
        return 'Desktop';
      };
      let ipAddress = 'Unknown';
      try {
        const ipRes = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipRes.json();
        ipAddress = ipData.ip || 'Unknown';
      } catch { /* ignore */ }

      await supabase.from('login_history').insert({
        user_id: user.id,
        user_agent: userAgent,
        device_type: getDeviceType(),
        browser: getBrowser(),
        login_method: 'password',
        ip_address: ipAddress,
      });

      supabase.functions.invoke('telegram-notify', {
        body: {
          type: 'new_login',
          firstName: user.user_metadata?.first_name || user.email?.split('@')[0] || '',
          lastName: user.user_metadata?.last_name || '',
          email: user.email || '',
          ipAddress,
          userAgent,
          deviceType: getDeviceType(),
          browser: getBrowser(),
        }
      }).catch((e: any) => console.error('Telegram login notify failed:', e));
    } catch (logError) {
      console.error('Error logging login:', logError);
    }
  };

  const finishLogin = async (user: any) => {
    await logLoginHistory(user);
    const userName = user?.user_metadata?.name || user?.email?.split('@')[0] || 'there';
    const displayName = userName.charAt(0).toUpperCase() + userName.slice(1);
    toast(`Welcome back, ${displayName}`, {
      duration: 4000,
      description: 'Ready to discover extraordinary stays?',
      style: {
        background: 'hsl(220 13% 10%)',
        color: 'hsl(45 60% 92%)',
        border: '1px solid hsl(43 74% 52% / 0.35)',
        boxShadow: '0 8px 40px hsl(43 74% 52% / 0.15), 0 2px 8px rgba(0,0,0,0.5)',
        borderRadius: '12px',
      },
      icon: '◆',
    });
    onOpenChange(false);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const result = signInSchema.safeParse({ email, password });
    if (!result.success) {
      setError(result.error.errors[0].message);
      setLoading(false);
      return;
    }

    const { email: validEmail, password: validPassword } = result.data;
    const { data, error } = await supabase.auth.signInWithPassword({ email: validEmail, password: validPassword });
    setLoading(false);

    if (error) { setError(error.message); return; }

    if (data.user) {
      const { data: mfaData } = await supabase.auth.mfa.listFactors();
      const verifiedFactor = mfaData?.totp?.find(f => (f.status as string) === 'verified');
      if (verifiedFactor) {
        setMfaFactorId(verifiedFactor.id);
        setPendingUser(data.user);
        setView('mfa');
      } else {
        await finishLogin(data.user);
      }
    }
  };

  const onVerifyMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaFactorId || mfaCode.length !== 6) return;
    setMfaError(null);
    setLoading(true);
    const codeToVerify = mfaCode;
    setMfaCode('');

    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({ factorId: mfaFactorId });
      if (challengeError) throw challengeError;
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: challengeData.id,
        code: codeToVerify,
      });
      if (verifyError) throw verifyError;
      await finishLogin(pendingUser);
    } catch (err: any) {
      const msg = err.message ?? '';
      const isInvalid = /invalid|totp|incorrect|wrong|expired/i.test(msg);
      setMfaError(isInvalid ? 'Incorrect code — enter the current 6-digit code' : msg || 'Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const onForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    const result = emailSchema.safeParse(forgotEmail);
    if (!result.success) { setForgotError(result.error.errors[0].message); return; }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-password-reset', {
        body: { email: result.data, redirectTo: 'https://exora.club/reset-password' },
      });
      if (error) throw error;
      if (data && !data.success) {
        if (data.reason === 'not_registered') setForgotError('This email is not registered.');
        else if (data.reason === 'not_approved') setForgotError('Account pending approval.');
        else setForgotError(data.message || 'Unable to send reset email.');
        return;
      }
      setView('forgot-success');
    } catch {
      setForgotError('Unable to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < 8) { setError('Minimum 8 characters'); return; }

    setLoading(true);
    setError(null);
    try {
      const token = getSearchParam('token');
      const { error: verifyError } = await supabase.auth.verifyOtp({ token_hash: token!, type: 'recovery' });
      if (verifyError) throw new Error('Invalid or expired link.');

      const { data, error: fnError } = await supabase.functions.invoke('set-user-password', { body: { password: newPassword } });
      if (fnError) throw new Error(fnError.message || 'Failed to set password');
      if (data?.error) throw new Error(data.error);

      toast.success('Password set successfully');
      await supabase.auth.signOut();
      setView('sign-in');
      setNewPassword(''); setConfirmPassword('');
    } catch (err: any) {
      setError(err.message || 'Failed to set password.');
    } finally {
      setLoading(false);
    }
  };

  const isLongEnough = newPassword.length >= 8;
  const passwordsMatch = confirmPassword.length > 0 && newPassword === confirmPassword;
  const passwordsMismatch = touched.confirm && confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canResetSubmit = isLongEnough && passwordsMatch;

  const contextLabel = view === 'sign-in' ? 'Sign in' : view === 'forgot-password' ? 'Password recovery' : view === 'forgot-success' ? 'Email sent' : view === 'reset-password' ? 'New password' : 'Verification';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="border border-border/60 text-foreground w-full max-w-[calc(100vw-24px)] sm:max-w-[calc(100vw-32px)] md:max-w-[420px] p-0 max-h-[90vh] overflow-y-auto rounded-2xl"
        style={{ background: 'hsl(0 0% 6%)' }}
      >
        <DialogTitle className="sr-only">{contextLabel}</DialogTitle>

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-5 right-5 text-muted-foreground hover:text-foreground transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="px-6 sm:px-8 pt-7 sm:pt-8 pb-0">
          <DialogHeader className="space-y-2 relative">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-[10px] uppercase tracking-[0.35em] text-gold/60 font-medium">{contextLabel}</p>
            </div>
          </DialogHeader>
        </div>

        {/* Divider */}
        <div className="mx-6 sm:mx-8 my-5 h-px bg-gradient-to-r from-transparent via-border/60 to-transparent" />

        <div className="relative z-10">
          <AnimatePresence mode="wait">

            {/* ═══ SIGN IN ═══ */}
            {view === 'sign-in' && (
              <motion.div key="sign-in" {...viewVariants} className="px-6 sm:px-8 pb-7 sm:pb-8">
                <div className="mb-7">
                  <h2 className="text-xl font-semibold text-foreground tracking-tight">
                    Welcome back
                  </h2>
                  <DialogDescription className="text-muted-foreground text-sm mt-1">
                    Access your reservations and saved stays
                  </DialogDescription>
                </div>

                <form onSubmit={onSubmit} className="space-y-5" name="signin-form">
                  <div className="space-y-1.5">
                    <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground/70 font-medium">
                      Email <span className="text-gold ml-0.5">*</span>
                    </label>
                    <input
                      type="email" name="email" placeholder="your@email.com"
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      className={inputCls} style={inputFontStyle}
                      required autoComplete="email"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground/70 font-medium">
                      Password <span className="text-gold ml-0.5">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"} name="password"
                        placeholder="Enter your password"
                        value={password} onChange={(e) => setPassword(e.target.value)}
                        className={inputCls + " pr-11"} style={inputFontStyle}
                        required autoComplete="current-password"
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground/40 hover:text-foreground transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end -mt-2">
                    <button type="button" onClick={() => setView('forgot-password')}
                      className="text-[10px] uppercase tracking-[0.15em] text-gold/45 hover:text-gold transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>

                  {error && (
                    <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                      className="rounded-xl px-4 py-3 border border-destructive/20 bg-destructive/[0.04]">
                      <p className="text-[12px] text-destructive">{error}</p>
                    </motion.div>
                  )}

                  <div className="pt-1">
                    <GoldCTA disabled={loading} loading={loading}>
                      {loading ? 'Signing in' : 'Sign in'}
                    </GoldCTA>
                  </div>
                </form>

                {/* Divider */}
                <div className="mx-0 my-6 h-px bg-gradient-to-r from-transparent via-border/60 to-transparent" />

                <p className="text-center text-[13px] text-muted-foreground/50">
                  Don't have access?{" "}
                  <button onClick={() => { onOpenChange(false); onRequestAccess(); }}
                    className="text-gold/60 hover:text-gold font-medium transition-colors"
                  >
                    Request access
                  </button>
                </p>
              </motion.div>
            )}

            {/* ═══ FORGOT PASSWORD ═══ */}
            {view === 'forgot-password' && (
              <motion.div key="forgot" {...viewVariants} className="px-6 sm:px-8 pb-7 sm:pb-8">
                <button onClick={() => { setView('sign-in'); setForgotError(''); }}
                  className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] mb-5 text-muted-foreground/40 hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>

                <div className="mb-7">
                  <h2 className="text-xl font-semibold text-foreground tracking-tight">
                    Reset your password
                  </h2>
                  <p className="text-muted-foreground text-sm mt-1">
                    We'll send a secure link to your email
                  </p>
                </div>

                <form onSubmit={onForgotSubmit} className="space-y-5">
                  <div className="space-y-1.5">
                    <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground/70 font-medium">
                      Email <span className="text-gold ml-0.5">*</span>
                    </label>
                    <input type="email" placeholder="your@email.com"
                      value={forgotEmail} onChange={(e) => { setForgotEmail(e.target.value); setForgotError(''); }}
                      className={inputCls} style={inputFontStyle} autoFocus
                    />
                    {forgotError && <p className="text-[11px] text-destructive px-0.5">{forgotError}</p>}
                  </div>

                  <div className="pt-1">
                    <GoldCTA disabled={loading} loading={loading}>
                      {loading ? 'Sending' : 'Send reset link'}
                    </GoldCTA>
                  </div>
                </form>
              </motion.div>
            )}

            {/* ═══ FORGOT SUCCESS ═══ */}
            {view === 'forgot-success' && (
              <motion.div key="forgot-success" {...viewVariants} className="px-6 sm:px-8 pb-7 sm:pb-8">
                {/* Success icon */}
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
                  className="flex justify-center mb-6"
                >
                  <div className="w-16 h-16 rounded-full border border-gold/30 flex items-center justify-center" style={{ background: 'hsl(var(--gold) / 0.08)' }}>
                    <Check className="w-7 h-7 text-gold" strokeWidth={1.5} />
                  </div>
                </motion.div>

                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-foreground tracking-tight mb-2">
                    Check your inbox
                  </h2>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    A secure reset link has been sent to
                  </p>
                  <p className="text-sm font-medium text-foreground mt-1" style={{ overflowWrap: 'anywhere' }}>
                    {forgotEmail}
                  </p>
                </div>

                <div className="border border-border/40 rounded-xl px-5 py-4 mb-6" style={{ background: 'hsl(0 0% 4%)' }}>
                  <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
                    <span className="text-gold text-xs">✦</span>
                    Link expires in <span className="text-gold font-semibold">24 hours</span>
                  </p>
                </div>

                <GoldCTA type="button" onClick={() => setView('sign-in')}>
                  Return to sign in
                </GoldCTA>

                <div className="text-center mt-4">
                  <button onClick={() => setView('forgot-password')}
                    className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground/40 hover:text-gold transition-colors"
                  >
                    Didn't receive it? Send again
                  </button>
                </div>
              </motion.div>
            )}

            {/* ═══ RESET PASSWORD ═══ */}
            {view === 'reset-password' && (
              <motion.div key="reset" {...viewVariants} className="px-6 sm:px-8 pb-7 sm:pb-8">
                <div className="mb-7">
                  <h2 className="text-xl font-semibold text-foreground tracking-tight">
                    Set new password
                  </h2>
                  <p className="text-muted-foreground text-sm mt-1">
                    Choose a strong, unique password
                  </p>
                </div>

                <form onSubmit={onResetSubmit} className="space-y-5">
                  <div className="space-y-1.5">
                    <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground/70 font-medium">
                      New password <span className="text-gold ml-0.5">*</span>
                    </label>
                    <div className="relative">
                      <input type={showPassword ? 'text' : 'password'}
                        value={newPassword} onChange={(e) => { setNewPassword(e.target.value); setTouched(t => ({ ...t, password: true })); }}
                        placeholder="Min 8 characters" required minLength={8}
                        className={inputCls + " pr-11"} style={inputFontStyle}
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground/40 hover:text-foreground transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <span className={`text-[11px] px-0.5 ${touched.password && !isLongEnough ? 'text-destructive' : isLongEnough ? 'text-emerald-500' : 'text-muted-foreground/40'}`}>
                      {isLongEnough ? '✓ Meets minimum length' : 'At least 8 characters'}
                    </span>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground/70 font-medium">
                      Confirm password <span className="text-gold ml-0.5">*</span>
                    </label>
                    <input type={showPassword ? 'text' : 'password'}
                      value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value); setTouched(t => ({ ...t, confirm: true })); }}
                      placeholder="Re-enter your password" required minLength={8}
                      className={`${inputCls} ${passwordsMismatch ? 'border-destructive/40 ring-1 ring-destructive/30' : passwordsMatch ? 'border-emerald-500/30 ring-1 ring-emerald-500/20' : ''}`}
                      style={inputFontStyle}
                    />
                    {passwordsMismatch && <span className="text-[11px] text-destructive flex items-center gap-1 px-0.5"><X className="w-3 h-3" /> Passwords do not match</span>}
                    {passwordsMatch && <span className="text-[11px] text-emerald-500 flex items-center gap-1 px-0.5"><Check className="w-3 h-3" /> Passwords match</span>}
                  </div>

                  {error && (
                    <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                      className="rounded-xl px-4 py-3 border border-destructive/20 bg-destructive/[0.04]">
                      <p className="text-[12px] text-destructive">{error}</p>
                    </motion.div>
                  )}

                  <div className="pt-1">
                    <GoldCTA disabled={loading || !canResetSubmit} loading={loading}>
                      {loading ? 'Setting password' : 'Set password & continue'}
                    </GoldCTA>
                  </div>
                </form>
              </motion.div>
            )}

            {/* ═══ MFA ═══ */}
            {view === 'mfa' && (
              <motion.div key="mfa" {...viewVariants} className="px-6 sm:px-8 pb-7 sm:pb-8">
                <div className="mb-7">
                  <h2 className="text-xl font-semibold text-foreground tracking-tight">
                    Two-step verification
                  </h2>
                  <p className="text-muted-foreground text-sm mt-1">
                    Enter the code from your authenticator app
                  </p>
                </div>

                <form onSubmit={onVerifyMfa} className="space-y-5">
                  <div className="space-y-1.5">
                    <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground/70 font-medium">
                      Verification code
                    </label>
                    <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                      placeholder="000000" value={mfaCode}
                      onChange={(e) => { setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setMfaError(null); }}
                      className="w-full rounded-md bg-background/50 border border-border/50 px-4 py-3 h-14 text-center text-xl tracking-[0.5em] font-mono text-foreground outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/20 transition-all placeholder:text-muted-foreground/30"
                      style={{ fontSize: '22px', caretColor: 'hsl(var(--gold))' }}
                      autoFocus autoComplete="one-time-code"
                    />
                  </div>

                  {mfaError && (
                    <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                      className="rounded-xl px-4 py-3 border border-destructive/20 bg-destructive/[0.04]">
                      <p className="text-[12px] text-destructive text-center">{mfaError}</p>
                    </motion.div>
                  )}

                  <div className="pt-1">
                    <GoldCTA disabled={loading || mfaCode.length !== 6} loading={loading}>
                      {loading ? 'Verifying' : 'Verify & continue'}
                    </GoldCTA>
                  </div>

                  <p className="text-center text-[10px] uppercase tracking-[0.15em] text-muted-foreground/40">
                    Open your authenticator app for the code
                  </p>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
};
